import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { useArquivosPendentes, type SituacaoPendente } from '../../shared/api/queries.ts';
import {
  DESFECHOS_DESCRITOS,
  DESFECHOS_ENCERRADOS,
  type ArquivoFiscal,
  type Cliente,
} from '../../shared/api/tipos.ts';
import {
  formatarData,
  formatarInteiro,
  formatarMoedaDeCentavos,
} from '../../shared/format.ts';
import { DialogoCupom } from '../arquivos-fiscais/DialogoCupom.tsx';
import { useSessao } from '../auth/sessao.tsx';
import { SeletorDeCliente } from './SeletorDeCliente.tsx';
import { TriagemPorCodigo } from './TriagemPorCodigo.tsx';

/** O que o seletor oferece; "todas" não vira filtro nenhum. */
type SituacaoEscolhida = 'todas' | SituacaoPendente;

/** O período digitado, que só vira consulta no Pesquisar. */
interface Periodo {
  readonly de: string;
  readonly ate: string;
}

/**
 * As notas de uma loja que ainda esperam alguém, com o motivo à vista.
 *
 * O período nasce no mês escolhido nas abas — chegar aqui pela linha de um
 * mês e ver nota de outro seria desorientador —, e esvaziar os dois campos
 * devolve o acervo inteiro: pendência antiga é justamente a que já foi
 * esquecida uma vez. Como em arquivos fiscais, as datas esperam o botão: esta
 * lista é de dezenas de milhares de linhas por competência, e cada tecla de
 * uma data digitada à mão viraria uma consulta.
 *
 * O valor aparece sem riscado porque nenhuma linha desta tabela entra na soma
 * do período; o rodapé diz isso uma vez, em vez de repetir a marca em cada
 * linha.
 */
export function XmlsPendentes({
  clientes,
  carregandoClientes,
  empresaId,
  aoEscolherCliente,
  periodoSugerido,
}: {
  clientes: readonly Cliente[];
  carregandoClientes: boolean;
  empresaId: string | null;
  aoEscolherCliente: (empresaId: string | null) => void;
  /** O mês escolhido nas abas, com que o filtro abre. */
  periodoSugerido: Periodo;
}) {
  const [situacao, setSituacao] = useState<SituacaoEscolhida>('todas');
  const [periodo, setPeriodo] = useState<Periodo>(periodoSugerido);
  // O cliente e a situação aplicam na hora — são escolhas, não algo digitado.
  const [aplicado, setAplicado] = useState<Periodo>(periodoSugerido);
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(50);
  const [cupom, setCupom] = useState<ArquivoFiscal | null>(null);

  const { usuario } = useSessao();
  // O contador vê a triagem e a lista; conferir e decidir são de quem atende
  // a loja — a permissão de verdade é a da rota, isto é só a tela não
  // oferecer o que responderia 403.
  const daEquipe = usuario?.papel !== 'contador';
  const cliente = clientes.find((candidato) => candidato.id === empresaId) ?? null;

  const { data, isPending, isError, error, isFetching } = useArquivosPendentes(
    {
      empresaId: empresaId ?? undefined,
      situacao: situacao === 'todas' ? undefined : situacao,
      de: aplicado.de === '' ? undefined : aplicado.de,
      ate: aplicado.ate === '' ? undefined : aplicado.ate,
    },
    { limite: porPagina, deslocamento: pagina * porPagina },
  );

  const periodoPendente = periodo.de !== aplicado.de || periodo.ate !== aplicado.ate;

  function pesquisar(evento?: FormEvent) {
    evento?.preventDefault();
    setPagina(0);
    setAplicado(periodo);
  }

  const arquivos = data?.itens ?? [];
  const total = data?.total ?? 0;
  const resumo = data?.resumo;

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent component="form" onSubmit={pesquisar}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'flex-start' }, flexWrap: 'wrap' }}
          >
            <SeletorDeCliente
              clientes={clientes}
              carregando={carregandoClientes}
              empresaId={empresaId}
              aoEscolher={(escolhido) => {
                setPagina(0);
                aoEscolherCliente(escolhido);
              }}
              ajuda="As notas são consultadas sempre por cliente."
            />

            <TextField
              select
              label="Situação"
              value={situacao}
              onChange={(evento) => {
                setPagina(0);
                setSituacao(evento.target.value as SituacaoEscolhida);
              }}
              sx={{ minWidth: 200 }}
              helperText="Pendente espera a SEFAZ; rejeitada voltou com erro."
            >
              <MenuItem value="todas">Todas as pendentes</MenuItem>
              <MenuItem value="pendente">Só pendentes</MenuItem>
              <MenuItem value="rejeitada">Só rejeitadas</MenuItem>
            </TextField>

            <TextField
              label="De"
              type="date"
              value={periodo.de}
              onChange={(evento) => setPeriodo((atual) => ({ ...atual, de: evento.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Em branco: o acervo inteiro da loja."
            />
            <TextField
              label="Até"
              type="date"
              value={periodo.ate}
              onChange={(evento) => setPeriodo((atual) => ({ ...atual, ate: evento.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchOutlinedIcon />}
              loading={isFetching}
              sx={{ mt: { md: 1 } }}
            >
              Pesquisar
            </Button>
          </Stack>

          {periodoPendente && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              O período digitado ainda não foi pesquisado — a lista abaixo é a do anterior.
            </Typography>
          )}
        </CardContent>
      </Card>

      {empresaId !== null && (
        <TriagemPorCodigo
          empresaId={empresaId}
          razaoSocial={cliente?.razaoSocial ?? ''}
          periodo={aplicado}
          daEquipe={daEquipe}
        />
      )}

      <Card>
        <CardContent>
          {empresaId === null ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              Escolha um cliente, ou clique numa linha da aba Clientes. A consulta de arquivo
              fiscal é sempre de uma loja.
            </Typography>
          ) : isError ? (
            <Alert severity="error">
              Não foi possível carregar as notas. {error instanceof Error ? error.message : ''}
            </Alert>
          ) : isPending ? (
            <Stack spacing={1}>
              {Array.from({ length: 5 }, (_, indice) => (
                <Skeleton key={indice} variant="rounded" height={40} />
              ))}
            </Stack>
          ) : arquivos.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              Nenhuma nota esperando neste cliente. Tudo o que chegou está autorizado ou
              cancelado.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Situação</TableCell>
                    <TableCell>Chave de acesso</TableCell>
                    <TableCell align="right">Número</TableCell>
                    <TableCell align="right">Série</TableCell>
                    <TableCell>Emissão</TableCell>
                    <TableCell>Desde</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Cupom</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {arquivos.map((arquivo) => (
                    <TableRow key={arquivo.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Chip
                          size="small"
                          color={arquivo.situacao === 'pendente' ? 'warning' : 'error'}
                          label={arquivo.situacao === 'pendente' ? 'Pendente' : 'Rejeitada'}
                        />
                      </TableCell>

                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                        >
                          {arquivo.chave}
                        </Typography>
                        {/* O motivo é o que resolve a nota, então ele mora
                            embaixo da chave e não numa coluna: é texto da
                            SEFAZ, de tamanho imprevisível. */}
                        {arquivo.motivo !== null && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', maxWidth: 420 }}
                          >
                            {arquivo.motivo}
                          </Typography>
                        )}
                        {/* O desfecho fica ao lado do motivo porque responde
                            a mesma pergunta: esta nota ainda espera alguém? */}
                        {arquivo.tratativa !== null && (
                          <Chip
                            size="small"
                            sx={{ mt: 0.5 }}
                            variant="outlined"
                            color={
                              DESFECHOS_ENCERRADOS.includes(arquivo.tratativa.desfecho)
                                ? 'success'
                                : 'info'
                            }
                            label={DESFECHOS_DESCRITOS[arquivo.tratativa.desfecho].rotulo}
                          />
                        )}
                      </TableCell>

                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {arquivo.numero}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {Number(arquivo.serie)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatarData(arquivo.emitidoEm)}
                      </TableCell>
                      {/* Desde quando ela está assim: é o que diz se a
                          pendência é de hoje à tarde ou de dois meses atrás. */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {arquivo.situacaoEm === null ? '—' : formatarData(arquivo.situacaoEm)}
                        {arquivo.conferencia !== null && (
                          <Tooltip
                            title={`A SEFAZ respondeu ${arquivo.conferencia.codigo} — ${arquivo.conferencia.mensagem}`}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              conferida em {formatarData(arquivo.conferencia.conferidaEm)}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                      >
                        {formatarMoedaDeCentavos(arquivo.valorCentavos)}
                      </TableCell>

                      <TableCell align="right">
                        <Tooltip title="Ver o cupom">
                          <IconButton
                            size="small"
                            onClick={() => setCupom(arquivo)}
                            aria-label={`Ver o cupom da nota ${arquivo.numero}`}
                          >
                            <ReceiptLongOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>

        {total > 0 && (
          <>
            <Divider />
            <TablePagination
              component="div"
              count={total}
              page={pagina}
              onPageChange={(_, nova) => setPagina(nova)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(evento) => {
                setPorPagina(Number(evento.target.value));
                setPagina(0);
              }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage="Por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
          </>
        )}
      </Card>

      {resumo !== undefined && resumo.quantidade > 0 && (
        <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', px: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{formatarInteiro(resumo.pendentes)}</strong> pendente(s) e{' '}
            <strong>{formatarInteiro(resumo.rejeitadas)}</strong> rejeitada(s). Nenhuma delas
            soma no período nem entra no <code>.zip</code> da contabilidade — entregá-las seria
            escriturar o que ainda não existe.
          </Typography>
        </Stack>
      )}

      <DialogoCupom arquivo={cupom} aoFechar={() => setCupom(null)} />
    </Stack>
  );
}
